import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Link,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";

const emptyForm = {
  dueDate: "",
  detail: "",
  status: "open",
  value: "",
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

const invoiceStatusLabel = (status) => {
  if (status === "paid") return "Pago";
  if (status === "open") return "Em aberto";
  return status || "-";
};

const CompanyInvoicesModal = ({ open, onClose, company }) => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadInvoices = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/company/${company.id}`);
      setInvoices(data.invoices || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [company?.id]);

  useEffect(() => {
    if (open && company?.id) {
      loadInvoices();
    }
  }, [open, company?.id, loadInvoices]);

  const handleOpenEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setEditForm({
      dueDate: invoice.dueDate
        ? moment(invoice.dueDate).format("YYYY-MM-DD")
        : "",
      detail: invoice.detail || "",
      status: invoice.status || "open",
      value: invoice.value ?? "",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedInvoice?.id) return;
    setSaving(true);
    try {
      await api.put(`/invoices/admin/${selectedInvoice.id}`, {
        dueDate: editForm.dueDate || null,
        detail: editForm.detail,
        status: editForm.status,
        value: Number(String(editForm.value).replace(",", ".")),
      });
      toast.success(i18n.t("compaies.invoices.toasts.updated"));
      setEditOpen(false);
      setSelectedInvoice(null);
      await loadInvoices();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete?.id) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/invoices/${confirmDelete.id}`);
      const messages = [];
      if (data.asaasCancelled) {
        messages.push("Asaas");
      } else if (data.asaasMessage) {
        toast.warn(`Asaas: ${data.asaasMessage}`);
      }
      if (data.mpCancelled) {
        messages.push("Mercado Pago");
      } else if (data.mpMessage) {
        toast.warn(`Mercado Pago: ${data.mpMessage}`);
      }
      if (data.efiCancelled) {
        messages.push("Gerencianet");
      } else if (data.efiMessage) {
        toast.warn(`Gerencianet: ${data.efiMessage}`);
      }
      if (messages.length) {
        toast.success(
          `${i18n.t("compaies.invoices.toasts.deleted")} (${messages.join(", ")})`
        );
      } else {
        toast.success(i18n.t("compaies.invoices.toasts.deleted"));
      }
      setConfirmDelete(null);
      await loadInvoices();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {i18n.t("compaies.invoices.title")} — {company?.name || ""}
          <IconButton
            size="small"
            style={{ float: "right" }}
            onClick={loadInvoices}
            disabled={loading}
          >
            <RefreshIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress size={32} />
            </div>
          ) : invoices.length === 0 ? (
            <Typography color="textSecondary" align="center">
              {i18n.t("compaies.invoices.empty")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">ID</TableCell>
                  <TableCell align="left">{i18n.t("compaies.invoices.detail")}</TableCell>
                  <TableCell align="center">{i18n.t("compaies.invoices.value")}</TableCell>
                  <TableCell align="center">{i18n.t("compaies.invoices.dueDate")}</TableCell>
                  <TableCell align="center">{i18n.t("compaies.invoices.status")}</TableCell>
                  <TableCell align="center">Asaas</TableCell>
                  <TableCell align="center">MP</TableCell>
                  <TableCell align="center">Efí</TableCell>
                  <TableCell align="center">{i18n.t("compaies.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell align="center">{inv.id}</TableCell>
                    <TableCell align="left">{inv.detail || "-"}</TableCell>
                    <TableCell align="center">{formatMoney(inv.value)}</TableCell>
                    <TableCell align="center">
                      {inv.dueDate
                        ? moment(inv.dueDate).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {invoiceStatusLabel(inv.status)}
                    </TableCell>
                    <TableCell align="center">
                      {inv.asaasPaymentId ? (
                        inv.linkInvoice && inv.linkInvoice.includes("asaas") ? (
                          <Link
                            href={inv.linkInvoice}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {inv.asaasPaymentId}
                          </Link>
                        ) : (
                          inv.asaasPaymentId
                        )
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {inv.mpPaymentId || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {inv.efiTxId || "-"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(inv)}
                        aria-label="edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => setConfirmDelete(inv)}
                        aria-label="delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            {i18n.t("compaies.invoices.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("compaies.invoices.editTitle")} #{selectedInvoice?.id}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            label={i18n.t("compaies.invoices.detail")}
            fullWidth
            value={editForm.detail}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, detail: e.target.value }))
            }
          />
          <TextField
            margin="dense"
            label={i18n.t("compaies.invoices.value")}
            type="number"
            fullWidth
            value={editForm.value}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, value: e.target.value }))
            }
          />
          <TextField
            margin="dense"
            label={i18n.t("compaies.invoices.dueDate")}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={editForm.dueDate}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, dueDate: e.target.value }))
            }
          />
          <TextField
            margin="dense"
            select
            label={i18n.t("compaies.invoices.status")}
            fullWidth
            value={editForm.status}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <MenuItem value="open">Em aberto</MenuItem>
            <MenuItem value="paid">Pago</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>
            {i18n.t("compaies.invoices.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
          >
            {i18n.t("compaies.table.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={i18n.t("compaies.invoices.deleteTitle")}
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      >
        {i18n.t("compaies.invoices.deleteMessage")}
        {confirmDelete?.asaasPaymentId ||
        confirmDelete?.mpPaymentId ||
        confirmDelete?.efiTxId ? (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {i18n.t("compaies.invoices.deleteGatewayHint")}
          </Typography>
        ) : null}
      </ConfirmationModal>
    </>
  );
};

export default CompanyInvoicesModal;
